import React, { Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import store from '../../store'
import { ROUTES } from '../../utils/routes'
import { enqueueErrorSnackbar } from '../../redux/actions/notificationActions'
import { useUser } from '../../utils'
import { Role } from 'common/api/v1/types'
import { ConfirmDialogProvider } from '../common/ConfirmDialog'
import { GlobalSearchProvider } from '../common/GlobalSearch/GlobalSearchProvider'
import { ContentDialogProvider } from '../common/ContentDialog'
import { mixpanel, usePageTracking } from '../../mixpanel'
import { Loading } from 'common-ui/components/Loading'

const EventManagerContainer = React.lazy(() => import('./EventManagerContainer'))
const NetworkManagerContainer = React.lazy(() => import('./NetworkManagerContainer'))
const StreamManagerContainer = React.lazy(() => import('./StreamManagerContainer'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 0,
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      store.dispatch(enqueueErrorSnackbar({ error, operation: query.meta?.operation }))
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _v, _, mutation) => {
      store.dispatch(enqueueErrorSnackbar({ error, operation: mutation.meta?.operation }))
    },
  }),
})

const LoggedIn = () => {
  const user = useUser()
  mixpanel.addUserInfo(user)
  usePageTracking()

  return (
    <QueryClientProvider client={queryClient}>
      <ConfirmDialogProvider>
        <ContentDialogProvider>
          <GlobalSearchProvider>
            <Suspense fallback={<Loading fullScreen />}>
              <Routes>
                <Route path={`${ROUTES.stream}/*`} element={<StreamManagerContainer />} />
                <Route path={`${ROUTES.events}/*`} element={<EventManagerContainer />} />
                <Route
                  path="/*"
                  element={
                    user.role === Role.basic ? <Navigate to={ROUTES.stream} replace /> : <NetworkManagerContainer />
                  }
                />
              </Routes>
            </Suspense>
          </GlobalSearchProvider>
        </ContentDialogProvider>
      </ConfirmDialogProvider>
    </QueryClientProvider>
  )
}

export default LoggedIn
